import { Injectable, computed, signal } from '@angular/core';
import { UI_STRINGS, UiKey } from '../content/ui-strings';
import { Locale } from '../models/content.model';

@Injectable({ providedIn: 'root' })
export class LanguageService {

	readonly supported = Object.keys(UI_STRINGS) as readonly Locale[];

	private readonly _lang = signal<Locale>(this.initialLang());
	readonly lang = this._lang.asReadonly();

	private readonly strings = computed(() => UI_STRINGS[this._lang()]);

	setLang(lang: Locale): void {
		localStorage.setItem('lang', lang);
		this._lang.set(lang);
	}

	t(key: UiKey, params?: Record<string, string | number>): string {
		const value = this.strings()[key];
		if (!params) return value;
		return value.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
			name in params ? String(params[name]) : match,
		);
	}

	private coerce(value: string | null | undefined): Locale {
		const needle = (value ?? '').toLowerCase();
		return this.supported.find(locale => locale.toLowerCase() === needle) ?? 'en';
	}

	private initialLang(): Locale {
		const url = new URL(location.href);
		const [fragment, fragmentQuery] = url.hash.split('?');
		const hashLang = new URLSearchParams(fragmentQuery).get('lang');
		const fromUrl = url.searchParams.get('lang') ?? hashLang;
		if (!fromUrl) return this.coerce(localStorage.getItem('lang'));

		url.searchParams.delete('lang');
		if (hashLang !== null) url.hash = fragment;
		history.replaceState(history.state, '', url);

		const lang = this.coerce(fromUrl);
		localStorage.setItem('lang', lang);
		return lang;
	}
}
